import React from 'react';
import { setIn, useFormikContext } from 'formik';

import MenuItem from '~/components/core/Atomic/MenuItem';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import type { AvailableWorkflowsResponse, WorkflowRuleParams } from '~/server_shared/openapi/data-contracts';

import TextFieldFormik from '../../../TextFieldFormik';
import useDataFetcher from '../../../useDataFetcher';

interface WorkflowSelectorProps {
  organizationId: number;
  id: keyof WorkflowRuleParams;
  label: string;
  disabled?: boolean;
  className?: string;
  eventKey?: string;
}

const WorkflowSelector: React.FC<WorkflowSelectorProps> = ({
  organizationId,
  id,
  label,
  disabled = false,
  className,
  eventKey,
}) => {
  const { setValues } = useFormikContext<WorkflowRuleParams & Record<string, unknown>>();

  // Build API URL with proper query parameter encoding
  const apiUrl = eventKey
    ? `/api/v1/automation/workflow_engine/rules/${organizationId}/available_workflows?event_key=${eventKey}`
    : null;

  const { isLoading, isError, data: workflowsResponse } = useDataFetcher<AvailableWorkflowsResponse>(apiUrl);
  const workflows = workflowsResponse?.workflows || [];

  // Listen for workflow_id changes and update workflow_name accordingly
  useFormikChangeListener({
    listenForKeys: [id],
    onChange: (newValues: WorkflowRuleParams) => {
      const selectedWorkflowId = newValues[id];
      const selectedWorkflow = workflows.find((workflow) => workflow.id === selectedWorkflowId);

      // Create updated values object with workflow_name set
      const updatedValues = setIn(newValues, 'workflow_name', selectedWorkflow?.name || '');
      setValues(updatedValues);
    },
    runOnFirstRender: false,
  });

  // Don't show loading spinner if no event is selected
  if (!eventKey) {
    return (
      <TextFieldFormik id={id} label={label} select fullWidth disabled={disabled} className={className}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <MenuItem value="" disabled>
          Select an event first
        </MenuItem>
      </TextFieldFormik>
    );
  }

  return (
    <TextFieldFormik id={id} label={label} select fullWidth disabled={disabled} className={className}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {isLoading ? (
        <MenuItem value="" disabled>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            Loading workflows...
          </div>
        </MenuItem>
      ) : isError ? (
        <MenuItem value="" disabled>
          Error loading workflows
        </MenuItem>
      ) : workflows && workflows.length > 0 ? (
        workflows.map((workflow) => (
          <MenuItem key={workflow.id} value={workflow.id}>
            {workflow.name}
          </MenuItem>
        ))
      ) : (
        <MenuItem value="" disabled>
          No workflows available for this event
        </MenuItem>
      )}
    </TextFieldFormik>
  );
};

export default WorkflowSelector;
